import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { P } from "const/permissions";
import { getApiError } from "utils/apiError";
import { formatDisplayDateTime } from "utils/dateFormat";
import { hasPermission } from "utils/permissionUtils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInvoiceEmailHistory } from "utils/invoiceEmailService";

/**
 * Send status + history for one invoice: every email logged under the
 * Invoice:<id> tag, newest first — same record the Client Emails tab shows.
 * Readers with email access can jump straight to a send in the Emails tab.
 */
const InvoiceEmailHistoryDialog = ({ open, onClose, invoiceId, invoiceNumber, clientId }) => {
  const navigate = useNavigate();
  const canViewEmails = hasPermission(P.EMAIL.MESSAGE_READ);
  const [page, setPage] = useState(0);
  const pageSize = 20;
  const { data, isFetching, isError, error } = useInvoiceEmailHistory(invoiceId, page, pageSize, {
    enabled: open && Boolean(invoiceId),
    keepPreviousData: true,
  });

  const openInEmailsTab = (emailId) => {
    if (!clientId || !emailId) return;
    navigate(`/admin/client/view/${clientId}?tab=emails&emailId=${emailId}`);
  };

  const rows = data?.content || [];
  const total = data?.totalItems || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Email history {invoiceNumber ? `— ${invoiceNumber}` : `— invoice ${invoiceId || ""}`}
      </DialogTitle>
      <DialogContent dividers>
        {isFetching && rows.length === 0 ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading send history…
            </Typography>
          </Stack>
        ) : isError ? (
          <Alert severity="error">
            {getApiError(error, "Could not load the send history. Please try again.")}
          </Alert>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No invoice emails tracked yet. Sends from here on are logged with their status,
            recipient, sender and time — and also appear under the client&apos;s Emails tab and the
            dashboard Emails view.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {total} send{total === 1 ? "" : "s"} tracked. Each row is one email attempt with its
              delivery status.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sent at</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Sent by</TableCell>
                  <TableCell align="right">Status</TableCell>
                  {canViewEmails && clientId && <TableCell align="right">Email</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{formatDisplayDateTime(row.sentAt)}</TableCell>
                    <TableCell>{row.recipientEmail || "-"}</TableCell>
                    <TableCell>{row.subject || "-"}</TableCell>
                    <TableCell>{row.sentByName || row.sentByEmail || "-"}</TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={row.status || "UNKNOWN"}
                        color={row.status === "SUCCESS" ? "success" : "error"}
                        variant={row.status === "SUCCESS" ? "outlined" : "filled"}
                      />
                      {row.errorMessage && (
                        <Typography variant="caption" color="error" display="block">
                          {row.errorMessage}
                        </Typography>
                      )}
                    </TableCell>
                    {canViewEmails && clientId && (
                      <TableCell align="right">
                        <Tooltip title="Open in Emails tab">
                          <IconButton
                            size="small"
                            onClick={() => openInEmailsTab(row.id)}
                            aria-label="Open in Emails tab"
                          >
                            <OpenInNewOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {total > pageSize && (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Button
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(p - 1, 0))}
                >
                  Previous
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Page {page + 1} of {Math.max(data?.totalPages || 1, 1)}
                </Typography>
                <Button
                  size="small"
                  disabled={page + 1 >= (data?.totalPages || 1)}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceEmailHistoryDialog;
